import { useState } from 'react';
import { omit } from 'lodash';
import { Icon, } from '@zapier/design-system/dist/components/display/Icon';
import { Link } from '@zapier/design-system/dist/components/navigation/Link';
import { Animation, Colors, Shadows, Typography, } from '@zapier/design-system/dist/theme';
import { isolateComponent, labelStyles, withModifier, } from '@zapier/common-utils';
export const DefaultButtonColors = {
    blue: {
        base: {
            backgroundColor: Colors.blue500,
            color: Colors.white,
        },
        disabled: {
            backgroundColor: Colors.grey100,
            color: Colors.grey500,
        },
        hover: {
            backgroundColor: Colors.blue700,
            color: Colors.white,
        },
        focus: {
            backgroundColor: Colors.blue500,
            outlineColor: Colors.blue500,
            color: Colors.white,
        },
    },
    black: {
        base: {
            backgroundColor: Colors.black,
            color: Colors.white,
        },
        disabled: {
            backgroundColor: Colors.grey100,
            color: Colors.grey500,
        },
        hover: {
            backgroundColor: Colors.grey900,
            color: Colors.white,
        },
        focus: {
            backgroundColor: Colors.black,
            outlineColor: Colors.grey700,
            color: Colors.white,
        },
    },
    grey: {
        base: {
            backgroundColor: Colors.grey100,
            color: Colors.blue500,
        },
        disabled: {
            backgroundColor: Colors.grey100,
            color: Colors.grey500,
        },
        hover: {
            backgroundColor: Colors.blue500,
            color: Colors.white,
        },
        focus: {
            backgroundColor: Colors.blue500,
            outlineColor: Colors.blue500,
            color: Colors.white,
        },
    },
    greyAlt: {
        base: {
            backgroundColor: Colors.grey100,
            color: Colors.black,
        },
        disabled: {
            backgroundColor: Colors.grey100,
            color: Colors.grey500,
        },
        hover: {
            backgroundColor: Colors.black,
            color: Colors.white,
        },
        focus: {
            backgroundColor: Colors.black,
            outlineColor: Colors.black,
            color: Colors.white,
        },
        selected: {
            backgroundColor: Colors.black,
            color: Colors.white,
        },
    },
    orange: {
        base: {
            backgroundColor: Colors.red500,
            color: Colors.white,
        },
        disabled: {
            backgroundColor: Colors.grey100,
            color: Colors.grey500,
        },
        hover: {
            backgroundColor: Colors.red600,
            color: Colors.white,
        },
        focus: {
            backgroundColor: Colors.red500,
            outlineColor: Colors.red500,
            color: Colors.white,
        },
    },
    white: {
        base: {
            backgroundColor: Colors.white,
            color: Colors.black,
            borderColor: Colors.grey400,
        },
        disabled: {
            backgroundColor: Colors.grey100,
            borderColor: Colors.transparent,
            color: Colors.grey500,
        },
        hover: {
            backgroundColor: Colors.white,
            color: Colors.blue500,
            borderColor: Colors.grey400,
        },
        focus: {
            backgroundColor: Colors.white,
            borderColor: Colors.blue500,
            outlineColor: Colors.blue500,
            color: Colors.blue500,
        },
    },
};
const buttonHeights = {
    medium: 50,
    small: 40,
    xsmall: 30,
};
function getTag(props) {
    if (props.presentationOnly) {
        return 'span';
    }
    if (props.htmlFor && !props.disabled) {
        return 'label';
    }
    if (props.href && !props.disabled) {
        return Link;
    }
    return 'button';
}
const Styles = labelStyles(__filename, {
    root: (props) => [
        Object.assign(Object.assign({ alignItems: 'center', appearance: 'none', borderRadius: 10, borderStyle: 'solid', borderWidth: 1, borderColor: 'transparent', boxSizing: 'border-box', position: 'relative', cursor: 'pointer', display: 'inline-flex' }, Typography.base), { fontWeight: 700, justifyContent: 'center', lineHeight: 1.3, margin: 0, minWidth: 0, opacity: 1, textAlign: 'center', textDecoration: 'none', textTransform: 'none', transitionProperty: 'box-shadow, border-color, background-color', transitionTimingFunction: Animation.transitionTimingFunction, transitionDuration: Animation.transitionDuration, verticalAlign: 'middle', whiteSpace: 'nowrap', '&::-moz-focus-inner': {
                padding: 0,
                border: 0,
            }, '&::before': {
                content: '""',
                position: 'absolute',
                pointerEvents: 'none',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                border: '2px solid transparent',
                borderRadius: 12,
                transitionProperty: 'all',
                transitionTimingFunction: Animation.transitionTimingFunction,
                transitionDuration: Animation.transitionDuration,
            } }),
        withModifier(props.color, Object.assign({}, props.colorTheme[props.color].base)),
        props.isSelected &&
            withModifier('selected', Object.assign(Object.assign({}, props.colorTheme[props.color].selected), { '&::before': {
                    content: 'none',
                } })),
        withModifier(props.color, {
            '&:hover:not([disabled])': Object.assign({}, props.colorTheme[props.color].hover),
            '&:focus:not([disabled])': Object.assign(Object.assign({}, omit(props.colorTheme[props.color].focus, 'outlineColor')), { '&::before': {
                    borderColor: props.colorTheme[props.color].focus.outlineColor,
                    top: -5,
                    right: -5,
                    bottom: -5,
                    left: -5,
                } }),
        }),
        props.casing &&
            withModifier(props.casing, {
                textTransform: props.casing,
            }),
        props.disabled &&
            withModifier('disabled', Object.assign(Object.assign({}, props.colorTheme[props.color].disabled), { cursor: 'not-allowed' })),
        props.isFullWidth &&
            withModifier('fullWidth', {
                width: '100%',
            }),
        props.isPill &&
            withModifier('pill', {
                borderRadius: 100,
                '&::before': {
                    borderRadius: 'inherit',
                },
            }),
        props.hasShadow &&
            withModifier('shadowed', {
                boxShadow: Shadows.elevation5,
            }),
        withModifier(props.size, {
            height: buttonHeights[props.size],
            fontSize: {
                xsmall: 12,
                small: 14,
                medium: 16,
            }[props.size],
        }),
        props.isSquare &&
            withModifier('square', {
                width: buttonHeights[props.size],
                fontSize: {
                    xsmall: 14,
                    small: 14,
                    medium: 16,
                }[props.size],
            }),
        props.borderRadiusSide &&
            withModifier(`borderRadiusSide-${props.borderRadiusSide}`, {
                borderRadius: {
                    left: '10px 0 0 10px',
                    right: '0 10px 10px 0',
                }[props.borderRadiusSide],
                '&::before': {
                    borderRadius: {
                        left: '12px 0 0 12px',
                        right: '0 12px 12px 0',
                    }[props.borderRadiusSide],
                },
            }),
    ],
    buttonContent: (props) => [
        {
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
        },
        withModifier(props.size, {
            xsmall: { padding: '0 20px' },
            small: { padding: '0 20px' },
            medium: { padding: '0 30px' },
        }[props.size]),
        !props.isPadded &&
            withModifier('unpadded', {
                padding: 0,
            }),
        props.isSquare &&
            withModifier('square', {
                padding: 0,
            }),
    ],
    buttonText: {
        display: 'inline-block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        flex: 1,
    },
    buttonIcon: ({ position }) => [
        {
            display: 'block',
            flex: 'none',
        },
        position === 'before' &&
            withModifier(position, {
                marginRight: 8,
            }),
        position === 'after' &&
            withModifier(position, {
                marginLeft: 8,
            }),
    ],
});
const sizeToIconSize = {
    xsmall: 18,
    small: 24,
    medium: 24,
};
const ButtonIcon = (props) => (isolateComponent("span", { css: Styles.buttonIcon(props) },
    isolateComponent(Icon, { canAcceptPointerEvents: false, isBlock: true, name: props.iconName, size: props.size })));
const ButtonText = props => (isolateComponent("span", { css: Styles.buttonText }, props.children));
function ButtonContent(props) {
    return (isolateComponent("span", { css: Styles.buttonContent(props) },
        props.iconBefore && (isolateComponent(ButtonIcon, { iconName: props.iconBefore, position: "before", size: sizeToIconSize[props.size] })),
        isolateComponent(ButtonText, null, props.children),
        props.iconAfter && (isolateComponent(ButtonIcon, { iconName: props.iconAfter, position: "after", size: sizeToIconSize[props.size] }))));
}
export function Button(props) {
    const ButtonTag = getTag(props);
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const { children, href, htmlFor, id, ariaLabel, target, rel, type, disabled, ariaDescribedBy, onClick, presentationOnly, onKeyDown, ariaExpanded, ariaControls, ariaHaspopup, ariaHidden, tabIndex, } = props;
    const role = disabled && href ? 'button' : undefined;
    const derivedState = {
        isFocused: !disabled && isFocused,
        isHovered: !disabled && isHovered,
    };
    const Props = !presentationOnly
        ? {
            type,
        }
        : {};
    const linkProps = href && !disabled && !presentationOnly
        ? {
            ariaLabel,
            href,
            rel,
            target,
        }
        : {};
    return (isolateComponent(ButtonTag, Object.assign({ "aria-controls": ariaControls, "aria-describedby": ariaDescribedBy, "aria-disabled": disabled, "aria-expanded": ariaExpanded, "aria-haspopup": ariaHaspopup, "aria-hidden": ariaHidden, "aria-label": ariaLabel, css: Styles.root(props), disabled: disabled, htmlFor: htmlFor, id: id, onBlur: () => setIsFocused(false), onClick: onClick, onFocus: () => setIsFocused(true), onKeyDown: onKeyDown, onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), role: role, tabIndex: tabIndex }, Props, linkProps), typeof children === 'function' ? (children(Object.assign(Object.assign({}, derivedState), { ButtonContent,
        ButtonText,
        ButtonIcon }))) : (isolateComponent(ButtonContent, Object.assign({}, props)))));
}
Button.defaultProps = {
    color: 'blue',
    colorTheme: DefaultButtonColors,
    disabled: false,
    isFullWidth: false,
    isPadded: true,
    isPill: false,
    size: 'medium',
};
//# sourceMappingURL=Button.js.map
